import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDataManipulation } from "../hooks/usedatamanipulation";
import { getTimezoneAbbr } from "./jsonrenderer";
import { getCoursesOptions, sortCourses } from "./dropdowns";
import { parseDateOnlyValue } from "./utils";


/*const fetchCoursesOptions = () => {
  return [
    'SMA1', 'SMA2', 'SMA3', 'SMA4', 'SMA5',
    'SMA6', 'SMA7', 'SMA8', 'SMA9', 'SMA10',
    'SMA11', 'SMA12', 'SMA13'
  ];
} */
// Helper functions to reduce duplication in render logic
const renderInputField = (
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  className = "flex-grow-mr10"
) => (
  <div className="flex-center-mb6">
    {label && <label className="field-label mr5">{label}:</label>}
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={className}
    />
  </div>
);

const renderSelectField = (
  value,
  onChange,
  options,
  placeholder = "",
  className = "mr10",
  ariaLabel = ""
) => (
  <select
    value={value}
    onChange={onChange}
    className={className}
    aria-label={ariaLabel}
  >
    <option value="">{placeholder}</option>
    {options.map((option) => (
      <option key={option.value || option} value={option.value || option}>
        {option.label || option}
      </option>
    ))}
  </select>
);

const renderButton = (
  text,
  onClick,
  className = "btn-green",
  disabled = false
) => (
  <button
    type="button"
    onClick={onClick}
    className={className}
    disabled={disabled}
  >
    {text}
  </button>
);

// Import the conversion functions from JsonRenderer
const convert12To24 = (hour12, period) => {
  const normalizedPeriod = period === "AM" || period === "PM" ? period : "AM";
  let hour24 = parseInt(hour12, 10);
  if (normalizedPeriod === "AM") {
    if (hour24 === 12) hour24 = 0;
  } else { // PM
    if (hour24 !== 12) hour24 += 12;
  }
  return hour24.toString().padStart(2, "0");
};

const convert24To12 = (hour24) => {
  const hour = parseInt(hour24, 10);
  const period = hour >= 12 ? "PM" : "AM";
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12: hour12.toString(), period };
};

const formatTime12Hour = (time24) => {
  const [hour, minute] = time24.split(":");
  const { hour12, period } = convert24To12(hour);
  return `${hour12}:${minute} ${period}`;
};

const renderTimeSelectors12Hour = (
  startHour,
  setStartHour,
  startMinute,
  setStartMinute,
  startPeriod,
  setStartPeriod,
  endHour,
  setEndHour,
  endMinute,
  setEndMinute,
  endPeriod,
  setEndPeriod
) => {
  const hourOptions12 = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString()
  );
  const minuteOptions = ["00", "15", "30", "45"];
  const periodOptions = ["AM", "PM"];

  return (
    <>
      {renderSelectField(
        startHour,
        (e) => setStartHour(e.target.value),
        hourOptions12,
        "HH",
        "mr5",
        "Select start hour"
      )}
      {renderSelectField(
        startMinute,
        (e) => setStartMinute(e.target.value),
        minuteOptions,
        "MM",
        "mr10",
        "Select start minute"
      )}
      {renderSelectField(
        startPeriod,
        (e) => setStartPeriod(e.target.value),
        periodOptions,
        "AM/PM",
        "mr10",
        "Select start period"
      )}
      <span className="mr10">to</span>
      {renderSelectField(
        endHour,
        (e) => setEndHour(e.target.value),
        hourOptions12,
        "HH",
        "mr5",
        "Select end hour"
      )}
      {renderSelectField(
        endMinute,
        (e) => setEndMinute(e.target.value),
        minuteOptions,
        "MM",
        "mr10",
        "Select end minute"
      )}
      {renderSelectField(
        endPeriod,
        (e) => setEndPeriod(e.target.value),
        periodOptions,
        "AM/PM",
        "mr10",
        "Select end period"
      )}
    </>
  );
};

const renderTimeSelectors = (
  startHour,
  setStartHour,
  startMinute,
  setStartMinute,
  endHour,
  setEndHour,
  endMinute,
  setEndMinute
) => {
  const hourOptions = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const minuteOptions = ["00", "15", "30", "45"];

  return (
    <>
      {renderSelectField(
        startHour,
        (e) => setStartHour(e.target.value),
        hourOptions,
        "HH",
        "mr5",
        "Select start hour"
      )}
      {renderSelectField(
        startMinute,
        (e) => setStartMinute(e.target.value),
        minuteOptions,
        "MM",
        "mr10",
        "Select start minute"
      )}
      <span className="mr10">to</span>
      {renderSelectField(
        endHour,
        (e) => setEndHour(e.target.value),
        hourOptions,
        "HH",
        "mr5",
        "Select end hour"
      )}
      {renderSelectField(
        endMinute,
        (e) => setEndMinute(e.target.value),
        minuteOptions,
        "MM",
        "mr10",
        "Select end minute"
      )}
    </>
  );
};

// Generic ArrayEditor component for handling arrays of primitives or objects
export const ArrayEditor = ({
  value = [],
  onChange,
  path,
  title,
  itemType = "primitive",
  objectFields = [],
  data,
  hideAddSection = false,
}) => {
  const [newItem, setNewItem] = useState(itemType === "object" ? {} : "");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState(
    itemType === "object" ? {} : ""
  );

  const { updateDataAtPath } = useDataManipulation(data, onChange);

  const updateArray = (newArray) => onChange(updateDataAtPath(path, newArray));

  const handleAdd = () => {
    if (itemType === "primitive") {
      if (!newItem.trim()) return;
      updateArray([...value, newItem]);
      setNewItem("");
    } else {
      if (Object.keys(newItem).length === 0) return;
      updateArray([...value, newItem]);
      setNewItem({});
    }
  };

  const handleRemove = (index) => {
    const newArray = [...value];
    newArray.splice(index, 1);
    updateArray(newArray);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditingItem(itemType === "object" ? { ...value[index] } : value[index]);
  };

  const handleSaveEdit = () => {
    const newArray = [...value];
    newArray[editingIndex] = editingItem;
    updateArray(newArray);
    setEditingIndex(null);
    setEditingItem(itemType === "object" ? {} : "");
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingItem(itemType === "object" ? {} : "");
  };

  const updateNewItemField = (field, value) => {
    if (itemType === "object") {
      setNewItem((prev) => ({ ...prev, [field]: value }));
    }
  };

  const updateEditingItemField = (field, value) => {
    if (itemType === "object") {
      setEditingItem((prev) => ({ ...prev, [field]: value }));
    } else {
      setEditingItem(value);
    }
  };

  return (
    <div className="array-editor">
      {title && <h5>{title}</h5>}

      {editingIndex !== null ? (
        <div className="editing-section">
          {itemType === "object"
            ? objectFields.map((field) =>
                renderInputField(
                  field,
                  editingItem[field] || "",
                  (e) =>
                    updateEditingItemField(
                      field,
                      e.target.type === "number"
                        ? Number(e.target.value)
                        : e.target.value
                    ),
                  field.includes("Value") || field.includes("Min")
                    ? "number"
                    : "text",
                  "",
                  "flex-grow-mr10"
                )
              )
            : renderInputField(
                null,
                editingItem,
                (e) => setEditingItem(e.target.value),
                "text",
                "",
                "flex-grow-mr10"
              )}
          <div className="flex-center-mb10">
            {renderButton("Save", handleSaveEdit, "btn-green mr10")}
            {renderButton("Cancel", handleCancelEdit, "btn-red")}
          </div>
        </div>
      ) : (
        <>
          <ul className="ul-none">
            {value.map((item, index) => (
              <li key={index} className="flex-center-mb6">
                {itemType === "object" ? (
                  <pre className="flex-grow-mr10">
                    {JSON.stringify(item, null, 2)}
                  </pre>
                ) : (
                  renderInputField(
                    null,
                    item,
                    (e) => {
                      const newArray = [...value];
                      newArray[index] = e.target.value;
                      updateArray(newArray);
                    },
                    "text",
                    "",
                    "flex-grow-mr10"
                  )
                )}
                {renderButton("Edit", () => handleEdit(index), "btn-blue mr5")}
                {renderButton("Remove", () => handleRemove(index), "btn-red")}
              </li>
            ))}
          </ul>

          {!hideAddSection && (
            <div className="add-section">
              {itemType === "object"
                ? objectFields.map((field) =>
                    renderInputField(
                      null,
                      newItem[field] || "",
                      (e) =>
                        updateNewItemField(
                          field,
                          e.target.type === "number"
                            ? Number(e.target.value)
                            : e.target.value
                        ),
                      field.includes("Value") || field.includes("Min")
                        ? "number"
                        : "text",
                      field,
                      "mr10"
                    )
                  )
                : renderInputField(
                    null,
                    newItem,
                    (e) => setNewItem(e.target.value),
                    "text",
                    `Add ${title ? title.toLowerCase().slice(0, -1) : "item"}`,
                    "flex-grow-mr10"
                  )}
              {renderButton("Add", handleAdd, "btn-green")}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// CoursesMinEditor component
export const CoursesMinEditor = ({
  value = [],
  onChange,
  path,
  data,
  changedFields = new Set(),
  coursesOptions = [],
  useStaticCourses = false,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [dynamicCoursesOptions, setDynamicCoursesOptions] = useState([]);

  const { updateDataAtPath } = useDataManipulation(data, onChange);

  // Fetch courses options based on useStaticCourses
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const options = await getCoursesOptions(useStaticCourses);
        setDynamicCoursesOptions(options);
      } catch (error) {
        console.error('Error loading courses for minimum courses:', error.message);
      }
    };

    loadCourses();
  }, [useStaticCourses]);

  const toggleCollapse = () => setCollapsed(!collapsed);

  // Derive months between start and end dates
  const monthNames = React.useMemo(() => {
    const startDate = data.startDate;
    const endDate = data.endDate;
    if (!startDate || !endDate) return [];
    const start = parseDateOnlyValue(startDate);
    const end = parseDateOnlyValue(endDate);
    if (!start || !end || start > end) return [];

    const names = [];
    const current = new Date(start);
    current.setDate(1);
    while (current <= end) {
      names.push(current.toLocaleString('default', { month: 'short', year: 'numeric' }));
      current.setMonth(current.getMonth() + 1);
    }
    return names;
  }, [data.startDate, data.endDate]);

  const monthCount = monthNames.length;

  // Build the list of courses to show as rows
  const courseRows = React.useMemo(() => {
    const set = new Set();
    coursesOptions.forEach((c) => set.add(c));
    dynamicCoursesOptions.forEach((c) => set.add(c));
    value.forEach((monthObj = {}) => Object.keys(monthObj).forEach((c) => set.add(c)));
    return sortCourses(Array.from(set));
  }, [coursesOptions, dynamicCoursesOptions, value]);

  // Normalize value length to months (without mutating the source)
  const buildMonthsArray = () => {
    const arr = [];
    for (let i = 0; i < monthCount; i += 1) {
      arr.push({ ...(value[i] || {}) });
    }
    return arr;
  };

  const handleCountChange = (course, monthIndex, newValue) => {
    const bounded =
      newValue === "" ? 0 : Math.max(0, Math.min(100, Number(newValue) || 0));
    const monthsArray = buildMonthsArray();
    if (!monthsArray[monthIndex]) monthsArray[monthIndex] = {};
    monthsArray[monthIndex][course] = bounded;
    onChange(updateDataAtPath(path, monthsArray));
  };

  return (
    <div className="courses-min-editor box-container">
      <div className="section-header" onClick={toggleCollapse}>
        <h5>Minimum courses required for each month</h5>
        <span className="collapse-icon">{collapsed ? "+" : "-"}</span>
      </div>

      {!collapsed && (
        <>
          {monthCount === 0 ? (
            <p className="mb10">Set both Start Date and End Date to configure months.</p>
          ) : (
            <div className="courses-min-table-wrapper">
              <div className="courses-min-table-scroll">
                <table className="courses-min-table">
                  <thead>
                    <tr>
                      <th className="course-col">Course</th>
                      {monthNames.map((name, idx) => (
                        <th key={`month-${idx}`}>{name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {courseRows.map((course) => (
                      <tr key={course}>
                        <td className="course-name-cell">{course}</td>
                        {monthNames.map((_, monthIdx) => {
                          const currentVal = value?.[monthIdx]?.[course] ?? 0;
                          const changedKey = `${path.join(".")}.${monthIdx}.${course}`;
                          return (
                            <td key={`${course}-${monthIdx}`}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={currentVal}
                                onChange={(e) => handleCountChange(course, monthIdx, e.target.value)}
                                className={`width80 ${
                                  changedFields.has(changedKey) ? "changed-field" : ""
                                }`}
                                aria-label={`${course} count for ${monthNames[monthIdx]}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// CoursesListEditor component (kept as generic ArrayEditor)
export const CoursesListEditor = ({ value = [], onChange, path, data }) => {
  const [collapsed, setCollapsed] = useState(true);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className="courses-list-editor box-container">
      <div className="section-header" onClick={toggleCollapse}>
        <h5>Courses</h5>
        <span className="collapse-icon">{collapsed ? "+" : "-"}</span>
      </div>
      {!collapsed && (
        <ArrayEditor
          value={value}
          onChange={onChange}
          path={path}
          title={null} // Remove the "Courses" heading after the box
          itemType="primitive"
          data={data}
        />
      )}
    </div>
  );
};

// AvailabilityEditor component for provider availability inclusions and exclusions
export const AvailabilityEditor = ({
  availability,
  onChange,
  path,
  data,
  changedFields = new Set(),
  setNotification,
}) => {
  const [newExclusion, setNewExclusion] = useState({
    date: "",
    startTime: "",
    endTime: "",
  });

  // New state for new inclusion time range parts - per day
  const [newInclusionTimes, setNewInclusionTimes] = useState({});
  const [newInclusionPeriods, setNewInclusionPeriods] = useState({});
  const [newRoutingPracticeByDay, setNewRoutingPracticeByDay] = useState({});
  const [isPracticeRoutingEnabled, setIsPracticeRoutingEnabled] = useState(false);

  const { updateDataAtPath } = useDataManipulation(data, onChange);

  const weekdays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const providerIndex = path[1];
  const providerPath = path.slice(0, -1); // ['providers', index, 'provider']
  const providerData = data.providers?.[providerIndex]?.provider || {};
  const practiceRouting = providerData.practiceRouting || {};
  const providerLicenses = Array.isArray(providerData.license)
    ? providerData.license
    : [];
  const practiceOptions = [
    ...new Set(
      (data?.practices || [])
        .filter(
          (practice) =>
            practice &&
            providerLicenses.includes(practice.state)
        )
        .map((practice) => practice?.name)
        .filter((name) => typeof name === "string" && name.trim())
    ),
  ];

  useEffect(() => {
    setIsPracticeRoutingEnabled(providerData.practiceRouting !== undefined);
  }, [providerIndex, providerData.practiceRouting]);

  const updateInclusionsForDay = (day, newInclusions) => {
    const newAvailability = {
      ...availability,
      inclusions: {
        ...availability.inclusions,
        weekly: {
          ...availability.inclusions?.weekly,
          [day]: newInclusions,
        },
      },
    };
    onChange(updateDataAtPath(path, newAvailability));
  };

  const handlePracticeRoutingToggle = (enabled) => {
    setIsPracticeRoutingEnabled(enabled);
    const updatedProvider = { ...providerData };

    if (enabled) {
      if (updatedProvider.practiceRouting === undefined) {
        updatedProvider.practiceRouting = {};
      }
    } else {
      delete updatedProvider.practiceRouting;
    }

    onChange(updateDataAtPath(providerPath, updatedProvider));
  };

  const updatePracticeRoutingForDay = (day, practicesForDay) => {
    const updatedProvider = { ...providerData };
    const updatedRouting = { ...(updatedProvider.practiceRouting || {}) };

    if (practicesForDay.length > 0) {
      updatedRouting[day] = practicesForDay;
    } else {
      delete updatedRouting[day];
    }

    updatedProvider.practiceRouting = updatedRouting;
    onChange(updateDataAtPath(providerPath, updatedProvider));
  };

  const addPracticeRouting = (day) => {
    const selectedPractice = newRoutingPracticeByDay[day];
    if (!selectedPractice) return;

    const currentPractices = practiceRouting[day] || [];
    if (currentPractices.includes(selectedPractice)) return;

    const updatedPractices = [...currentPractices, selectedPractice].sort((a, b) =>
      a.localeCompare(b)
    );
    updatePracticeRoutingForDay(day, updatedPractices);
    setNewRoutingPracticeByDay((prev) => ({ ...prev, [day]: "" }));
  };

  const removePracticeRouting = (day, practiceName) => {
    const currentPractices = practiceRouting[day] || [];
    const updatedPractices = currentPractices.filter(
      (practice) => practice !== practiceName
    );
    updatePracticeRoutingForDay(day, updatedPractices);
  };

  // Format time range string from parts
  const formatTimeRange = (sh, sm, eh, em) => {
    if (!sh || !sm || !eh || !em) return "";
    return `${sh}:${sm}-${eh}:${em}`;
  };

  const addInclusion = (day) => {
    const dayTimes = newInclusionTimes[day] || {};
    const dayPeriods = newInclusionPeriods[day] || {};
    const startPeriod = dayPeriods.startPeriod || "AM";
    const endPeriod = dayPeriods.endPeriod || "AM";

    // Convert 12-hour to 24-hour format
    const startHour24 = convert12To24(dayTimes.startHour, startPeriod);
    const endHour24 = convert12To24(dayTimes.endHour, endPeriod);

    const newInclusionStr = formatTimeRange(
      startHour24,
      dayTimes.startMinute,
      endHour24,
      dayTimes.endMinute
    );
    if (!newInclusionStr) return;
    const currentInclusions = availability.inclusions?.weekly?.[day] || [];

    // Parse time range string to minutes for comparison
    const parseTimeRange = (range) => {
      const [start, end] = range.split("-");
      const [startH, startM] = start.split(":").map(Number);
      const [endH, endM] = end.split(":").map(Number);
      return { start: startH * 60 + startM, end: endH * 60 + endM };
    };

    // Check for overlapping inclusion time
    const newRange = parseTimeRange(newInclusionStr);
    for (const existing of currentInclusions) {
      const existingRange = parseTimeRange(existing);
      // Overlap if new start < existing end and new end > existing start
      if (
        newRange.start < existingRange.end &&
        newRange.end > existingRange.start
      ) {
        if (setNotification) {
          console.warn('Using setNotification for overlapping inclusion');
          setNotification({ type: 'error', message: 'Overlapping inclusion time detected for providers.' });
        } else {
          console.warn('Using alert for overlapping inclusion');
          alert("Overlapping inclusion time detected for providers.");
        }
        return;
      }
    }

    // Sort function for time ranges (e.g., "08:00-09:00")
    const sortTimeRanges = (a, b) => {
      const getStartTime = (range) => range.split("-")[0];
      return getStartTime(a).localeCompare(getStartTime(b));
    };

    const newInclusions = [...currentInclusions, newInclusionStr].sort(
      sortTimeRanges
    );

    updateInclusionsForDay(day, newInclusions);
    // Clear the time selectors for this day
    setNewInclusionTimes((prev) => ({
      ...prev,
      [day]: { startHour: "", startMinute: "", endHour: "", endMinute: "" },
    }));
    setNewInclusionPeriods((prev) => ({
      ...prev,
      [day]: { startPeriod: "", endPeriod: "" },
    }));
  };

  const removeInclusion = (day, index) => {
    const currentInclusions = availability.inclusions?.weekly?.[day] || [];
    updateInclusionsForDay(
      day,
      currentInclusions.filter((_, i) => i !== index)
    );
  };

  const updateExclusions = (newExclusions) => {
    const newAvailability = {
      ...availability,
      exclusions: {
        ...availability.exclusions,
        specificTime: newExclusions,
      },
    };
    onChange(updateDataAtPath(path, newAvailability));
  };

  const parseLocalDate = (ymd) => {
    if (!ymd || !ymd.includes("-")) return null;
    const [year, month, day] = ymd.split("-").map((v) => parseInt(v, 10));
    if (!year || !month || !day) return null;
    // Use local date constructor to avoid UTC shift (midnight rollback)
    return new Date(year, month - 1, day);
  };

  const ExclusionTimeSelectors = ({ startTime, endTime, onStartTimeChange, onEndTimeChange }) => {
    const [startHour, setStartHour] = useState(() => {
      if (startTime && startTime.includes(":")) {
        const [hour] = startTime.split(":");
        return hour ? convert24To12(hour).hour12 : "";
      }
      return "";
    });

    const [startMinute, setStartMinute] = useState(() => {
      if (startTime && startTime.includes(":")) {
        const [, minute] = startTime.split(":");
        return minute || "";
      }
      return "";
    });

    const [startPeriod, setStartPeriod] = useState(() => {
      if (startTime && startTime.includes(":")) {
        const [hour] = startTime.split(":");
        return hour ? convert24To12(hour).period : "";
      }
      return "";
    });

    const [endHour, setEndHour] = useState(() => {
      if (endTime && endTime.includes(":")) {
        const [hour] = endTime.split(":");
        return hour ? convert24To12(hour).hour12 : "";
      }
      return "";
    });

    const [endMinute, setEndMinute] = useState(() => {
      if (endTime && endTime.includes(":")) {
        const [, minute] = endTime.split(":");
        return minute || "";
      }
      return "";
    });

    const [endPeriod, setEndPeriod] = useState(() => {
      if (endTime && endTime.includes(":")) {
        const [hour] = endTime.split(":");
        return hour ? convert24To12(hour).period : "";
      }
      return "";
    });

    // Sync local state with prop changes
    useEffect(() => {
      if (startTime && startTime.includes(":")) {
        const [hour] = startTime.split(":");
        const [, minute] = startTime.split(":");
        setStartHour(hour ? convert24To12(hour).hour12 : "");
        setStartMinute(minute || "");
        setStartPeriod(hour ? convert24To12(hour).period : "");
      } else {
        setStartHour("");
        setStartMinute("");
        setStartPeriod("");
      }
    }, [startTime]);

    useEffect(() => {
      if (endTime && endTime.includes(":")) {
        const [hour] = endTime.split(":");
        const [, minute] = endTime.split(":");
        setEndHour(hour ? convert24To12(hour).hour12 : "");
        setEndMinute(minute || "");
        setEndPeriod(hour ? convert24To12(hour).period : "");
      } else {
        setEndHour("");
        setEndMinute("");
        setEndPeriod("");
      }
    }, [endTime]);

    // Update parent only when all time parts are selected
    const handleStartHourChange = (value) => {
      setStartHour(value);
      if (value && startMinute && startPeriod) {
        const newStartTime = `${convert12To24(value, startPeriod)}:${startMinute}`;
        onStartTimeChange(newStartTime);
      }
    };

    const handleStartMinuteChange = (value) => {
      setStartMinute(value);
      if (startHour && value && startPeriod) {
        const newStartTime = `${convert12To24(startHour, startPeriod)}:${value}`;
        onStartTimeChange(newStartTime);
      }
    };

    const handleStartPeriodChange = (value) => {
      setStartPeriod(value);
      if (startHour && startMinute && value) {
        const newStartTime = `${convert12To24(startHour, value)}:${startMinute}`;
        onStartTimeChange(newStartTime);
      }
    };

    const handleEndHourChange = (value) => {
      setEndHour(value);
      if (value && endMinute && endPeriod) {
        const newEndTime = `${convert12To24(value, endPeriod)}:${endMinute}`;
        onEndTimeChange(newEndTime);
      }
    };

    const handleEndMinuteChange = (value) => {
      setEndMinute(value);
      if (endHour && value && endPeriod) {
        const newEndTime = `${convert12To24(endHour, endPeriod)}:${value}`;
        onEndTimeChange(newEndTime);
      }
    };

    const handleEndPeriodChange = (value) => {
      setEndPeriod(value);
      if (endHour && endMinute && value) {
        const newEndTime = `${convert12To24(endHour, value)}:${endMinute}`;
        onEndTimeChange(newEndTime);
      }
    };

    return renderTimeSelectors12Hour(
      startHour, handleStartHourChange,
      startMinute, handleStartMinuteChange,
      startPeriod, handleStartPeriodChange,
      endHour, handleEndHourChange,
      endMinute, handleEndMinuteChange,
      endPeriod, handleEndPeriodChange
    );
  };

  const addExclusion = () => {
    if (!newExclusion.date) return;
    if (newExclusion.startTime && !newExclusion.endTime) {
      if (setNotification) {
        console.warn('Using setNotification for exclusion end time missing');
        setNotification({ type: 'error', message: 'Exclusion will be added when end time is also defined.' });
      } else {
        console.warn('Using alert for exclusion end time missing');
        alert("Exclusion will be added when end time is also defined.");
      }
      return;
    }
    const currentExclusions = availability.exclusions?.specificTime || [];
    updateExclusions([...currentExclusions, newExclusion]);
    setNewExclusion({ date: "", startTime: "", endTime: "" });
  };

  const removeExclusion = (index) => {
    const currentExclusions = availability.exclusions?.specificTime || [];
    updateExclusions(currentExclusions.filter((_, i) => i !== index));
  };

  // const updateExclusionField = (index, field, value) => {
  //   const currentExclusions = availability.exclusions?.specificTime || [];
  //   const updatedExclusions = currentExclusions.map((excl, i) =>
  //     i === index ? { ...excl, [field]: value } : excl
  //   );
  //   updateExclusions(updatedExclusions);
  // };

  return (
    <div className="availability-editor">
      <h3 className="field-name-Inclusion">Availability : </h3>
      <div className="object-field primitive-field mb16">
        <label className="field-name">Practice Routing:</label>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={isPracticeRoutingEnabled}
            onChange={(e) => handlePracticeRoutingToggle(e.target.checked)}
            className="primitive-field-input"
          />
          <span className="slider"></span>
        </label>
      </div>
      <div className="inclusions-section">
        {weekdays.map((day) => (
          <div key={day} className="weekday-inclusions">
            <h4 className="field-name-Inclusion capitalize">{day}  Availability </h4>
            <ul className="ul-none">
              {(availability.inclusions?.weekly?.[day] || []).map(
                (inc, idx) => {
                  const abbr = getTimezoneAbbr(
                    data.providers?.[providerIndex]?.provider?.timezone
                  );
                  const formattedInc = abbr
                    ? `${formatTime12Hour(inc.split("-")[0])} <strong>${abbr}</strong> - ${formatTime12Hour(inc.split("-")[1])} <strong>${abbr}</strong>`
                    : `${formatTime12Hour(inc.split("-")[0])} - ${formatTime12Hour(inc.split("-")[1])}`;
                  return (
                    <li
                      key={idx}
                      className={`daily-slot-item-custom ${
                        changedFields.has(
                          path
                            .concat(["inclusions", "weekly", day, idx])
                            .join(".")
                        )
                          ? "changed-field"
                          : ""
                      }`}
                    >
                      <span dangerouslySetInnerHTML={{ __html: formattedInc }}></span>
                      <button
                        type="button"
                        className="btn-red"
                        onClick={() => removeInclusion(day, idx)}
                      >
                        Remove
                      </button>
                    </li>
                  );
                }
              )}
            </ul>
            <div className="flex-center">
              {renderTimeSelectors12Hour(
                (newInclusionTimes[day] || {}).startHour || "",
                (value) =>
                  setNewInclusionTimes((prev) => ({
                    ...prev,
                    [day]: { ...prev[day], startHour: value },
                  })),
                (newInclusionTimes[day] || {}).startMinute || "",
                (value) =>
                  setNewInclusionTimes((prev) => ({
                    ...prev,
                    [day]: { ...prev[day], startMinute: value },
                  })),
                (newInclusionPeriods[day] || {}).startPeriod || "",
                (value) =>
                  setNewInclusionPeriods((prev) => ({
                    ...prev,
                    [day]: { ...prev[day], startPeriod: value },
                  })),
                (newInclusionTimes[day] || {}).endHour || "",
                (value) =>
                  setNewInclusionTimes((prev) => ({
                    ...prev,
                    [day]: { ...prev[day], endHour: value },
                  })),
                (newInclusionTimes[day] || {}).endMinute || "",
                (value) =>
                  setNewInclusionTimes((prev) => ({
                    ...prev,
                    [day]: { ...prev[day], endMinute: value },
                  })),
                (newInclusionPeriods[day] || {}).endPeriod || "",
                (value) =>
                  setNewInclusionPeriods((prev) => ({
                    ...prev,
                    [day]: { ...prev[day], endPeriod: value },
                  }))
              )}
              {renderButton("Add", () => addInclusion(day), "btn-green")}
            </div>
            {isPracticeRoutingEnabled && (
              <div className="object-field custom-layout primitive-field mb16">
                <label className="field-name-practiceRouting">Practice Routing ({day}):</label>
                <div className="practiceRouting-value-area license-value-column">
                  <div className="existing-items flex-wrap">
                    {(practiceRouting[day] || []).map((practiceName) => (
                      <span key={`${day}-${practiceName}`} className="license-item mr5 flex-center">
                        <span className="license-text">{practiceName}</span>
                        <button
                          type="button"
                          onClick={() => removePracticeRouting(day, practiceName)}
                          className="btn-remove ml2"
                          aria-label={`Remove ${practiceName} from ${day} routing`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="add-section-credential flex-start">
                    <select
                      value={newRoutingPracticeByDay[day] || ""}
                      onChange={(e) =>
                        setNewRoutingPracticeByDay((prev) => ({
                          ...prev,
                          [day]: e.target.value,
                        }))
                      }
                      className="add-select mr10"
                      aria-label={`Select practice routing for ${day}`}
                    >
                      <option value="">Select practice</option>
                      {practiceOptions
                        .filter((practiceName) => !(practiceRouting[day] || []).includes(practiceName))
                        .map((practiceName) => (
                          <option key={`${day}-opt-${practiceName}`} value={practiceName}>
                            {practiceName}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => addPracticeRouting(day)}
                      className="btn-add-practiceRouting"
                      disabled={!newRoutingPracticeByDay[day]}
                      aria-label={`Add practice routing for ${day}`}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="exclusions-section">
        <h5 className="field-name-Inclusion">Time Off : </h5>
        <div className="inclusions-section">
        <ul className="ul-none">
          {(availability.exclusions?.specificTime || []).map((excl, idx) => {
            // Format time range string for display
            const abbr = getTimezoneAbbr(
              data.providers?.[providerIndex]?.provider?.timezone
            );
            const timeRangeStr =
              excl.startTime && excl.endTime
                ? `${formatTime12Hour(excl.startTime)} <strong>${abbr}</strong> - ${formatTime12Hour(excl.endTime)} <strong>${abbr}</strong>`
                : "";
            // Format date to MM/DD/YYYY
            const formatDateMMDDYYYY = (dateStr) => {
              if (!dateStr) return "";
              const [year, month, day] = dateStr.split("-");
              return `${month}/${day}/${year}`;
            };
            const formattedDate = formatDateMMDDYYYY(excl.date);

            return (
              <li
                key={idx}
                className={`daily-slot-item`}
              >
                <span>
                  <span className="field-name-exclusion">Date:</span>{formattedDate}
                  {timeRangeStr ? <> <span className="field-name-exclusion">Time:</span><span dangerouslySetInnerHTML={{ __html: timeRangeStr }}></span></> : ''}
                </span>
                <button
                  type="button"
                  className="btn-red"
                  onClick={() => removeExclusion(idx)}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
        <div className="flex-center">
          <DatePicker
            selected={newExclusion.date ? parseLocalDate(newExclusion.date) : null}
            onChange={(date) => {
              if (date) {
                // Format to yyyy-mm-dd for storage
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, "0");
                const dd = String(date.getDate()).padStart(2, "0");
                const dateStr = `${yyyy}-${mm}-${dd}`;
                setNewExclusion((prev) => ({ ...prev, date: dateStr }));
              } else {
                setNewExclusion((prev) => ({ ...prev, date: "" }));
              }
            }}
            dateFormat="MM-dd-yyyy"
            placeholderText="MM-DD-YYYY"
            className="mr10"
            minDate={new Date()} // Prevent selecting past dates
          />
          <ExclusionTimeSelectors
            startTime={newExclusion.startTime}
            endTime={newExclusion.endTime}
            onStartTimeChange={(time) => setNewExclusion(prev => ({ ...prev, startTime: time }))}
            onEndTimeChange={(time) => setNewExclusion(prev => ({ ...prev, endTime: time }))}
          />
          {renderButton("Add", addExclusion, "btn-green")}
        </div>
      </div>
    </div>
    </div>
  );
};

// WeeklySlotsEditor component for adding/removing weekly slots with dropdown
export const WeeklySlotsEditor = ({ weeklySlots, onChange, path, data }) => {
  const [selectedDay, setSelectedDay] = useState("");
  const { updateDataAtPath } = useDataManipulation(data, onChange);
  const allDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const availableDays = allDays.filter((day) => !weeklySlots.includes(day));

  const handleAddDay = () => {
    if (!selectedDay) return;
    const newWeeklySlots = [...weeklySlots, selectedDay];
    onChange(updateDataAtPath(path, newWeeklySlots));
    setSelectedDay("");
  };

  const handleRemoveDay = (day) => {
    const newWeeklySlots = weeklySlots.filter((d) => d !== day);
    onChange(updateDataAtPath(path, newWeeklySlots));
  };

  return (
    <div className="weekly-slots-editor">
      <h5>Weekly Slots</h5>
      <div className="flex-center">
        {renderSelectField(
          selectedDay,
          (e) => setSelectedDay(e.target.value),
          availableDays.map((day) => ({
            value: day,
            label: day.charAt(0).toUpperCase() + day.slice(1),
          })),
          "Select a day",
          "mr10"
        )}
        {renderButton("Add", handleAddDay, "btn-green", !selectedDay)}
      </div>
      <ul className="ul-none">
        {weeklySlots.map((day, index) => (
          <li key={index} className="flex-center-mb6">
            <span className="flex-grow-mr10 capitalize">{day}</span>
            {renderButton("Remove", () => handleRemoveDay(day), "btn-red")}
          </li>
        ))}
      </ul>
    </div>
  );
};

// New component for Appointment Slots Editor to fix React hooks usage
export const AppointmentSlotsEditor = ({
  appointmentSlots,
  onChange,
  path,
  data,
}) => {
  const [newStartHour, setNewStartHour] = useState("");
  const [newStartMinute, setNewStartMinute] = useState("");
  const [newEndHour, setNewEndHour] = useState("");
  const [newEndMinute, setNewEndMinute] = useState("");

  const { updateDataAtPath } = useDataManipulation(data, onChange);

  const formatTime = (hour, minute) => {
    const hh = hour.toString().padStart(2, "0");
    const mm = minute.toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const handleAddDailySlot = () => {
    if (
      newStartHour === "" ||
      newStartMinute === "" ||
      newEndHour === "" ||
      newEndMinute === ""
    )
      return;
    const formattedSlot = `${formatTime(
      newStartHour,
      newStartMinute
    )}-${formatTime(newEndHour, newEndMinute)}`;
    const newDailySlots = [...(appointmentSlots.daily || []), formattedSlot];
    onChange(updateDataAtPath([...path, "daily"], newDailySlots));
    setNewStartHour("");
    setNewStartMinute("");
    setNewEndHour("");
    setNewEndMinute("");
  };

  const handleRemoveDailySlot = (index) => {
    const newDailySlots = [...(appointmentSlots.daily || [])];
    newDailySlots.splice(index, 1);
    onChange(updateDataAtPath([...path, "daily"], newDailySlots));
  };

  return (
    <div className="appointment-slots-container">
      <div className="appointment-slot-box">
        <h5>Daily Slots</h5>
        <ul>
          {(appointmentSlots.daily || []).map((slot, index) => (
            <li key={index} className="flex-center-mb6">
              <span className="flex-grow-mr10">{slot}</span>
              {renderButton(
                "Remove",
                () => handleRemoveDailySlot(index),
                "btn-red"
              )}
            </li>
          ))}
        </ul>
        <div className="flex-center">
          {renderTimeSelectors(
            newStartHour,
            setNewStartHour,
            newStartMinute,
            setNewStartMinute,
            newEndHour,
            setNewEndHour,
            newEndMinute,
            setNewEndMinute
          )}
          {renderButton("Add", handleAddDailySlot, "btn-green")}
        </div>
      </div>
      <div className="appointment-slot-box">
        <WeeklySlotsEditor
          weeklySlots={appointmentSlots.weekly || []}
          onChange={onChange}
          path={[...path, "weekly"]}
          data={data}
        />
      </div>
    </div>
  );
};
